// eslint:disable:jsx-no-lambda
import * as React from 'react';
import { MeatballMenu, PromptConfirmationModal, ValidationSummary, ComponentProps, withDropdownMenu, ButtonTertiary } from 'ui/common';
import { LeftPanel } from './panels/left-panel';
import { Permission } from 'ui/common/permission';
import { ButtonCallToAction, ValidationProps } from 'ui';
import { DropdownMenuContext } from 'ui/common/dropdown-menu/types';
import { Link, useSearchParams } from 'react-router-dom';
import { ShareCase } from 'app/area/case/features/share-case/share-case';
import * as navigate from 'app/area/shared/navigation';
import { ConfidentialBanner } from 'ui/info-banner';
import { ComplexValue, FailedDomainConstraintMetaData, UserMessage, UserType } from 'app/types-shared';

import styles from './case-header-panel.scss';
import { routeConstants } from 'app/routes/route-constants';
import { StickyHeader, StickyHeaderButtonContainer } from 'app/shell/components/sticky-header/sticky-header';
import { isValidUrl } from 'app/infrastructure/validation/url';
import { Link as ExternalLink } from 'ui';
import { SharedState } from 'app/types-shared';
import { getHostingPath } from 'app/infrastructure/hosting';
import { AddRiskEvaluationInitialValues } from 'app/area/case/features/loss-potential-evaluation/types';
import { AddLossPotentialModal } from 'app/area/case/components/loss-potential/add-loss-potential-evaluation-modal';
import { DefaultContainerProps } from 'app/infrastructure/redux';
import { ChangeCaseStatus, handleChangeStatusUrlParams } from 'app/components/change-case-status/change-case-status';
import { AddNewConnectedCasesWireData, ChangeCaseStatusWireData } from 'app/area/case/features/calls-to-actions/types';
import { AddNewConnectedCasesDropdown } from 'app/components/add-new-cases-dropdown/add-new-connected-cases-dropdown';
import { PortalTargetContext } from 'app/area/case-report/case-listing/portal-provider';
import { useSharedState } from 'app/infrastructure/redux/hooks';

export interface HeaderPanelProps extends ComponentProps, DefaultContainerProps {
  /**
   * Translations
   */
  textOpenCase?: string;
  textEditCase?: string;
  textDeleteCaseTitle?: string;
  textDeleteCaseDescription?: string;
  textDeleteCaseContinue?: string;
  textDeleteCaseCancel?: string;
  textDuplicateCaseTitle?: string;
  textOpenBowtieTitle?: string;
  textPrintCase?: string;
  textChangeCaseType?: string;
  textConfidentialTitle?: string;
  textConfidentialDescription?: string;
  textConnectCase?: string;
  textSubmit?: string;
  textOpenInSurveyManager?: string;
  textSubmitAndContinue?: string;
  textAddEvaluationTitle?: string;
  textAddEvaluationSubtitle?: string;
  textEvaluationAddItem?: string;
  textEvaluationDescription?: string;
  textEvaluationHideInReport?: string;  
  textEvaluationValidFrom?: string;
  textChangeStatus?: string;
  textChangeStatusWarningModalTitle?: string;
  textChangeStatusWarningModalDescription?: string;
  textChangeStatusWarningModalContinue?: string;
  textChangeStatusWarningModalEditCase?: string;
  textChangeStatusWithCaseId?: string;
  textChangeStatusSuccess?: string;
  textChangeStatusForReload?: string;
  textChangeStatusValidationError?: string;

  /**
   *
   * Content to include in the right panel
   */
  url?: string;
  contentRight?: any;
  onEditDocument?: () => void;
  onDeleteDocument?: () => void;
  onDuplicateDocument?: () => void;
  onLoadBowtie?: () => void;
  onChangeCaseType?: () => void;
  onConnectCase?: () => void;
  onSubmitDocument: (continueEditing?: boolean) => void;
  onCloseDocument: () => void;
  onLoadPermissions: (caseNumber: number) => void;
  permissionsLoaded?: boolean;
  disableAllEntityOperations?: boolean;
  enabledEntityOperations?: { edit: boolean; delete: boolean; bowtie: boolean; 
    lossPotential: boolean; residualLossPotential: boolean; };
  validation?: ValidationProps;
  readOnly?: boolean;
  caseId?: number;
  translate?: { [key: string]: string };
  confidential?: ComplexValue<boolean>;
  enableStickyHeader?: boolean;
  urlFromTemplateString?: string;
  sharedState: SharedState;
  onCopyRiskEvaluation?: (riskMatrixInstance: number, instanceCopyCmd: number, initialValues: AddRiskEvaluationInitialValues, 
    newWindow?: boolean) => void;
  callsToActions?: (caseId: number) => { changeStatus: ChangeCaseStatusWireData; addNewConnectedCases: AddNewConnectedCasesWireData[] };
  onChangeCaseStatus: (caseId: number, status: ComplexValue<number>) => 
    {userMessages: string | UserMessage[]; warnings: FailedDomainConstraintMetaData[]; validationErrors: string[]};
  reloadOnCallsToActions?: boolean;
}

const HeaderPanel: React.FC<React.PropsWithChildren<HeaderPanelProps>> = (props) => {
  const {
    componentId,
    className,
    validation,
    contentRight,
    disableAllEntityOperations,
    enableStickyHeader
  } = props;

  const leftPanelContents = <LeftPanel {...props} />;

  return <>
    <StickyHeader id={componentId} disabled={!enableStickyHeader} rightSideContent={!disableAllEntityOperations && contentRight}
      className={`${className} ${styles.headerWrapper} ${enableStickyHeader ? styles.headerSticky : ''} header-panel`}>
      {leftPanelContents}
    </StickyHeader>
    <div className={styles.validation}>
      <ValidationSummary validationError={validation} />
    </div>
  </>;
};

interface PrintLinkProps extends DropdownMenuContext {
  text: string;
  url: string;
}

export const PrintLink: any = withDropdownMenu((props: PrintLinkProps) => {
  const onPrintCase = () => {
    const { handleDropdownHide, url } = props;
    handleDropdownHide && handleDropdownHide();
    navigate.toPrintCaseView(url);
  };
  return <li>
    <button type="button" className="dropdown-item" onClick={onPrintCase}>
      {props.text}
    </button>
  </li>;
});

const ReadModeRightContent = (props: HeaderPanelProps, visibilityConfiguration: HeaderVisibilityConfiguration = {}) => {
  const panelConfig = visibilityConfiguration.panel;
  const meatballMenuConfig = visibilityConfiguration.meatballMenu;
  const { portalTarget } = React.useContext(PortalTargetContext);
  const caseURL = routeConstants.caseViewFor(props.caseId);
  const caseEditUrl = routeConstants.caseEditFor(props.caseId);
  const [callsToActions, setCallsToActions] = React.useState(undefined);

  const onExpandMenu = async() => {
    if (!props.permissionsLoaded && props.onLoadPermissions) {
      props.onLoadPermissions(props.caseId);
    }
    const getCallsToActions = await props.callsToActions?.(props.caseId);
    setCallsToActions(getCallsToActions);
  };
  
  const [searchParams, setSearchParams] = useSearchParams();
  React.useEffect(() => {
    handleChangeStatusUrlParams(props, searchParams, setSearchParams);
  }, []);

  const sharedState = useSharedState();
  const allowForCurrentUser = sharedState?.userType?.v !== UserType.basic;

  return <>  
    {panelConfig && <Permission requireAll="edit" permissions={props.enabledEntityOperations}>
      {panelConfig.edit && <StickyHeaderButtonContainer>
        <ButtonCallToAction id="editcasebutton"
          onClick={props.onEditDocument}>
          {props.textEditCase}
        </ButtonCallToAction>
      </StickyHeaderButtonContainer>}
    </Permission>}
    {meatballMenuConfig && <MeatballMenu onExpandMenu={onExpandMenu} portalTarget={portalTarget?.current}>
      <Permission requireAll="edit" permissions={props.enabledEntityOperations}>
        {meatballMenuConfig.edit && caseEditUrl &&
          <Link to={caseEditUrl} className="btn dropdown-item">{props.textEditCase}</Link>}
      </Permission>
      <Permission requireAll="lossPotential" permissions={props.enabledEntityOperations} isListElement={true}>
        {meatballMenuConfig.lossPotential && allowForCurrentUser && 
          <AddLossPotentialModal {...props} textValidFrom={props.textEvaluationValidFrom}
            textEvaluationDescription={props.textEvaluationDescription} textEvaluationHideInReport={props.textEvaluationHideInReport}
            textAddEvaluationSubtitle={`${props.textAddEvaluationSubtitle} ${' #'}${props.caseId}`}
            textAddItem={props.textAddEvaluationTitle} textAdd={props.textEvaluationAddItem} addButtonStyle='dropdownItem'
            allowCopyFromOtherInstance={props.enabledEntityOperations?.residualLossPotential}
            allowCopyEvaluationToResidual={props.enabledEntityOperations?.residualLossPotential} />}
      </Permission>
      {allowForCurrentUser && !!callsToActions?.addNewConnectedCases?.length  &&
        callsToActions?.addNewConnectedCases?.map((addNewConnectedCase: AddNewConnectedCasesWireData, index: number) => {
          return <AddNewConnectedCasesDropdown
            entityId={props.caseId}
            addNewConnectedCaseButtons={addNewConnectedCase.newCaseButtons}
            textLabelMenu={addNewConnectedCase.label}
            key={index}
            {...props} />;
        })
      }
      <Permission requireAll="edit" permissions={props.enabledEntityOperations}>
        {allowForCurrentUser && !!callsToActions?.changeStatus && 
          <ChangeCaseStatus
            caseId={props.caseId} changeCaseStatusOptions={callsToActions.changeStatus}
            changeCaseStatus={props.onChangeCaseStatus}
            textChangeStatus={props.textChangeStatus} textWarningModalTitle={props.textChangeStatusWarningModalTitle}
            textWarningModalDescription={props.textChangeStatusWarningModalDescription}
            textWarningModalContinue={props.textChangeStatusWarningModalContinue}
            textWarningModalEditCase={props.textChangeStatusWarningModalEditCase}
            textChangeStatusSuccess={props.textChangeStatusSuccess}
            textChangeStatusWithCaseId={props.textChangeStatusWithCaseId}
            textChangeStatusForReload={props.textChangeStatusForReload}
            textChangeStatusValidationError={props.textChangeStatusValidationError}
            reloadOnChangeStatus={props.reloadOnCallsToActions}
            allowWarnings={false} />
        }
      </Permission>
      {meatballMenuConfig.open && caseURL && <Link to={caseURL} className="btn dropdown-item">{props.textOpenCase}</Link>}
      <Permission requireAll="bowtie" permissions={props.enabledEntityOperations} isListElement={true}>
        {meatballMenuConfig.bowtie &&
          <button type="button" className="dropdown-item" onClick={props.onLoadBowtie}>
            {props.textOpenBowtieTitle}
          </button>}
      </Permission>
      <Permission requireAll="surveyManager" permissions={props.enabledEntityOperations} isListElement={true}>
        {props.sharedState.access &&
          props.sharedState.access.surveyManager.send &&
          isValidUrl(props.urlFromTemplateString) &&
          <ExternalLink url={props.urlFromTemplateString} linkText={props.textOpenInSurveyManager} className={'dropdown-item'} />}
      </Permission>
      <Permission requireAll="edit" permissions={props.enabledEntityOperations} isListElement={true}>
        {meatballMenuConfig.duplicate &&
          <button type="button" className="dropdown-item" onClick={props.onDuplicateDocument}>
            {props.textDuplicateCaseTitle}
          </button>}
      </Permission>
      <Permission requireAll="edit" permissions={props.enabledEntityOperations} isListElement={true}>
        {meatballMenuConfig.share &&
          <ShareCase {...props}>
            {
              ({ onOpen }) => <button type="button" className="dropdown-item" onClick={onOpen}>{props.translate.share_case}</button>
            }
          </ShareCase>
        }
      </Permission>
      {meatballMenuConfig.print &&
        <PrintLink text={props.textPrintCase} url={(getHostingPath() || '') + caseURL} />
      }
      <Permission requireAll="delete" permissions={props.enabledEntityOperations} isListElement={true}>
        {meatballMenuConfig.delete && <PromptConfirmationModal
          title={props.textDeleteCaseTitle}
          description={props.textDeleteCaseDescription}
          continueText={props.textDeleteCaseContinue}
          cancelText={props.textDeleteCaseCancel}
          onContinue={props.onDeleteDocument}>
          <button type="button" className="dropdown-item">{props.textDeleteCaseTitle}</button>
        </PromptConfirmationModal>}
      </Permission>
    </MeatballMenu>
    }
  </>;
};

export type HeaderVisibilityConfiguration = {
  panel?: {
    edit?: boolean;
  };
  meatballMenu?: {
    open?: boolean;
    edit?: boolean;
    delete?: boolean;
    duplicate?: boolean;
    bowtie?: boolean;
    changeCaseType?: boolean;
    share?: boolean;
    print?: boolean;
    submitAndContinue?: boolean;
    lossPotential?: boolean;
    residualLossPotential?: boolean;
  };
};

const EditModeRightContent = (props: HeaderPanelProps, visibilityConfiguration: HeaderVisibilityConfiguration = {}) => {
  const meatballMenuConfig = visibilityConfiguration.meatballMenu;
  const shouldDisplayChangeCaseType = !props.readOnly && meatballMenuConfig?.changeCaseType;
  const shouldDisplayMeatballMenu = shouldDisplayChangeCaseType || meatballMenuConfig?.submitAndContinue;
  const onSubmitAndContinue = () => {
    props.onSubmitDocument && props.onSubmitDocument(true);
  };
  const onSubmitDocument = () => {
    props.onSubmitDocument && props.onSubmitDocument(false);
  };

  return <>
    <StickyHeaderButtonContainer>
      <ButtonTertiary onClick={props.onCloseDocument}>
        {props.textDeleteCaseCancel}
      </ButtonTertiary>
      <ButtonCallToAction id="form-submit" onClick={onSubmitDocument}>
        {props.textSubmit}
      </ButtonCallToAction>
    </StickyHeaderButtonContainer>
    {
      shouldDisplayMeatballMenu &&
      <MeatballMenu>
        {shouldDisplayChangeCaseType && <button type="button" className="dropdown-item" onClick={props.onChangeCaseType}>
          {props.textChangeCaseType}
        </button>}
        {meatballMenuConfig.submitAndContinue && <button type="button" className="dropdown-item" onClick={onSubmitAndContinue}>
          {props.textSubmitAndContinue}
        </button>}
      </MeatballMenu>
    }
  </>;
};

export const ReadHeaderPanel = (props: any) => {
  const isConfidential = props.confidential && props.confidential.valueObject && props.confidential.valueObject.value;
  return (
    <>
      {!!isConfidential &&
        <ConfidentialBanner textTitle={props.textConfidentialTitle} textDescription={props.textConfidentialDescription} />}
      <HeaderPanel contentRight={ReadModeRightContent(props, props.headerVisibilityConfiguration)} {...props} />
    </>);
};

export const EditHeaderPanel = (props: any) => {
  return <HeaderPanel contentRight={EditModeRightContent(props, props.headerVisibilityConfiguration)} {...props} />;
};
